import type {
  DayOfWeek,
  NetworkComparisonMode,
  NetworkDashboardAvailableStore,
  NetworkDashboardCategories,
  NetworkDashboardCohortUnit,
  NetworkDashboardHr,
  NetworkDashboardLiveStoreCa,
  NetworkDashboardLosses,
  NetworkDashboardSales,
  NetworkDashboardStoreAgeFilter,
  NetworkDashboardTopProducts,
} from '@gozoki/api-types'

import { gozokiApiWithAuth } from '../gozokiApi'

export const fetchAccountingKpis = async (date: string) => {
  const response = await gozokiApiWithAuth.get(`/stores/get-accounting-kpis/${date}`) // 'AAAA-MM-DD'
  return response.data
}

export const fetchHoursData = async (
  days: DayOfWeek[],
  storeIds: number[],
  plage: 'AM' | 'PM' | 'DAY',
  dateStart: Date,
  dateEnd: Date,
) => {
  if (days.length === 0 || storeIds.length === 0) {
    return []
  }
  const response = await gozokiApiWithAuth.post('/stores/get-hours-data', {
    dateEnd: dateEnd.toDateString(),
    dateStart: dateStart.toDateString(),
    days,
    plage,
    storeIds,
  })
  return response.data
}

export const fetchTimeSpentData = async (
  days: DayOfWeek[],
  storeIds: number[],
  plage: 'AM' | 'PM' | 'DAY',
  dateStart: Date,
  dateEnd: Date,
) => {
  if (days.length === 0 || storeIds.length === 0) {
    return []
  }
  const response = await gozokiApiWithAuth.post('/stores/get-time-spent-data', {
    dateEnd: dateEnd.toDateString(),
    dateStart: dateStart.toDateString(),
    days,
    plage,
    storeIds,
  })
  return response.data
}

export interface FetchNetworkDashboardKpisParams {
  startDate?: string
  endDate?: string
  /**
   * Optional active-store baseline period. When set, "active stores" are computed from this window
   * instead of `startDate`/`endDate`.
   */
  activeStartDate?: string
  activeEndDate?: string
  cohortUnit?: NetworkDashboardCohortUnit
  cohortValue?: number
  comparisonMode?: NetworkComparisonMode
  /**
   * When defined (including empty string serialized), backend intersects with geographic `storeIds`
   * and skips opening-date SQL presets. Omit entirely for legacy preset-only filtering.
   */
  ageStoreIds?: number[]
}

// Sérialisation partagée des sections du dashboard Réseau (aussi utilisée par le BO via Tuyau).
export const buildNetworkDashboardParams = (
  storeIds: number[] | undefined,
  storeAgeFilter: NetworkDashboardStoreAgeFilter,
  period: FetchNetworkDashboardKpisParams | undefined,
): Record<string, string> => {
  const params: Record<string, string> = {}
  if (storeIds && storeIds.length > 0) params.storeIds = storeIds.join(',')
  params.storeAgeFilter = storeAgeFilter
  if (period?.startDate) params.startDate = period.startDate
  if (period?.endDate) params.endDate = period.endDate
  if (period?.activeStartDate) params.activeStartDate = period.activeStartDate
  if (period?.activeEndDate) params.activeEndDate = period.activeEndDate
  if (period?.cohortUnit) params.cohortUnit = period.cohortUnit
  if (period?.cohortValue !== undefined) params.cohortValue = String(period.cohortValue)
  if (period?.comparisonMode) params.comparisonMode = period.comparisonMode
  if (period?.ageStoreIds !== undefined) {
    params.ageStoreIds = period.ageStoreIds.length > 0 ? period.ageStoreIds.join(',') : ''
  }
  return params
}

type NetworkDashboardSectionPath =
  | '/stores/get-network-dashboard-sales'
  | '/stores/get-network-dashboard-losses'
  | '/stores/get-network-dashboard-categories'
  | '/stores/get-network-dashboard-top-products'
  | '/stores/get-network-dashboard-hr'

const fetchNetworkDashboardSection = async <T>(
  path: NetworkDashboardSectionPath,
  storeIds: number[] | undefined,
  storeAgeFilter: NetworkDashboardStoreAgeFilter,
  period: FetchNetworkDashboardKpisParams | undefined,
  signal?: AbortSignal,
): Promise<T> => {
  const params = buildNetworkDashboardParams(storeIds, storeAgeFilter, period)
  const response = await gozokiApiWithAuth.get<T>(path, {
    params: Object.keys(params).length > 0 ? params : undefined,
    signal,
  })
  return response.data
}

export const fetchNetworkDashboardSales = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  signal?: AbortSignal,
): Promise<NetworkDashboardSales> =>
  fetchNetworkDashboardSection(
    '/stores/get-network-dashboard-sales',
    storeIds,
    storeAgeFilter,
    period,
    signal,
  )

export const fetchNetworkDashboardLosses = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  signal?: AbortSignal,
): Promise<NetworkDashboardLosses> =>
  fetchNetworkDashboardSection(
    '/stores/get-network-dashboard-losses',
    storeIds,
    storeAgeFilter,
    period,
    signal,
  )

export const fetchNetworkDashboardCategories = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  signal?: AbortSignal,
): Promise<NetworkDashboardCategories> =>
  fetchNetworkDashboardSection(
    '/stores/get-network-dashboard-categories',
    storeIds,
    storeAgeFilter,
    period,
    signal,
  )

export const fetchNetworkDashboardTopProducts = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  signal?: AbortSignal,
): Promise<NetworkDashboardTopProducts> =>
  fetchNetworkDashboardSection(
    '/stores/get-network-dashboard-top-products',
    storeIds,
    storeAgeFilter,
    period,
    signal,
  )

export const fetchNetworkDashboardHr = (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
  period?: FetchNetworkDashboardKpisParams,
  signal?: AbortSignal,
): Promise<NetworkDashboardHr> =>
  fetchNetworkDashboardSection(
    '/stores/get-network-dashboard-hr',
    storeIds,
    storeAgeFilter,
    period,
    signal,
  )

export const fetchNetworkDashboardLiveStoreCa = async (
  storeIds?: number[],
  storeAgeFilter: NetworkDashboardStoreAgeFilter = 'default',
): Promise<NetworkDashboardLiveStoreCa> => {
  const params: Record<string, string> = {}
  if (storeIds && storeIds.length > 0) params.storeIds = storeIds.join(',')
  params.storeAgeFilter = storeAgeFilter
  const response = await gozokiApiWithAuth.get<NetworkDashboardLiveStoreCa>(
    '/stores/get-network-dashboard-live-store-ca',
    { params: Object.keys(params).length > 0 ? params : undefined },
  )
  return response.data
}

export const fetchNetworkDashboardAvailableStores = async (): Promise<
  NetworkDashboardAvailableStore[]
> => {
  const response = await gozokiApiWithAuth.get<NetworkDashboardAvailableStore[]>(
    '/stores/get-network-dashboard-available-stores',
  )
  return response.data
}

/**
 * Alertes comptables. `accounting.accounting.warnings` est une ressource de liaison `selection`
 * (audit du 19/09, M-5) : l'écran envoie la sélection de boutiques de l'utilisateur, le serveur la
 * borne au périmètre d'affectation. Sans sélection, le serveur décide (tout le périmètre).
 */
export const fetchAccountingWarnings = async (storeIds?: number[]) => {
  const response = await gozokiApiWithAuth.get('/stores/get-accounting-warnings', {
    params: storeIds && storeIds.length > 0 ? { storeIds } : undefined,
  })
  return response.data
}
